import { FC, useMemo } from 'react';
import { Input } from 'antd';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import classNames from 'classnames';
import { SkeletonCircle } from '@dynamika-shared/ui/Skeleton/Skeleton';

export type EventListItem = {
  key: string;
  title: string;
  description: string;
  count: number;
  selected: boolean;
};

export type EventItemProps = {
  item: EventListItem;
  isSelected: boolean;
  onSelect: (item: EventListItem) => void;
  loading: boolean;
};

export type EventsListProps = {
  items: EventListItem[];
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSelect: (item: EventListItem) => void;
  loading: boolean;
  error?: Error;
};

export const EventsList: FC<EventsListProps> = ({
  items,
  searchValue,
  onSearchChange,
  onSelect,
  loading,
  error,
}) => {
  const filteredItems = useMemo(() => {
    const value = searchValue?.trim().toLowerCase() ?? '';
    if (!value) {
      return items;
    }

    return items.filter(item => item.title.toLowerCase().includes(value));
  }, [items, searchValue]);

  return (
    <div className='bg-white border border-brightGray rounded-sm p-1.5 h-full flex flex-col'>
      <div>
        <div className='flex gap-2 items-center mb-2 border-vividTangelo border-l-[3px]'>
          <div className='bg-vividTangelo h-full rounded-sm w-[3px]' />
          <div className='flex flex-col'>
            <div className='font-sans text-1824 font-medium text-black'>
              Events
            </div>
            <p className='font-nunito text-1216 text-dimGray'>
              Choose an event to manage subscription
            </p>
          </div>
        </div>
        <Input
          value={searchValue}
          onChange={e => {
            onSearchChange?.(e.target.value);
          }}
          className='!mt-0 h-6 min-w-[32rem] !rounded-xl px-2'
          placeholder='Search events'
          suffix={
            <SearchSuffix
              disabled={!searchValue}
              action={() => {
                onSearchChange?.('');
              }}
            />
          }
        />
      </div>

      <div className='mx-1 my-2 border-t border-brightGray' />

      <div className='flex flex-col gap-[8px]'>
        {error ? (
          <div className='flex items-center justify-center px-1.5 py-0.5 rounded-xl bg-white'>
            <div className='font-sans text-1420 text-red-500'>
              {error.message || 'Failed to fetch events'}
            </div>
          </div>
        ) : (
          filteredItems.map(item => {
            const isSelected = item.selected;
            return (
              <EventItem
                key={item.key}
                item={item}
                isSelected={isSelected}
                onSelect={onSelect}
                loading={loading}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

const EventItem: FC<EventItemProps> = ({
  item,
  isSelected,
  onSelect,
  loading,
}) => {
  return (
    <div
      key={item.key}
      onClick={() => onSelect?.(item)}
      className={classNames(
        'flex items-center justify-between px-1.5 py-1 rounded-sm cursor-pointer',
        {
          'bg-waterLight': isSelected,
          'bg-white hover:bg-waterLightHover50': !isSelected,
        }
      )}
    >
      <div className='flex flex-col'>
        <div className='font-sans text-1624 font-medium text-black'>
          {item.title}
        </div>
        <div className='font-nunito text-1216 text-dimGray'>
          {item.description}
        </div>
      </div>
      {loading ? (
        <SkeletonCircle className='w-4 h-4' />
      ) : (
        <div
          className={classNames(
            'flex items-center justify-center px-1.5 py-0.5 rounded-xl',
            {
              'bg-white': isSelected,
              'bg-waterLightHover50': !isSelected,
            }
          )}
        >
          <div className='font-sans text-1420 text-black'>{item.count}</div>
        </div>
      )}
    </div>
  );
};
